// EventActionsTab — list of actions linked to the current event, with link/unlink.
// Data comes from GET /events/{event_id}/actions.
// Event owns this relationship via action_links (linked_to_type='event').

import { useState, useMemo } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { eventsApi, type LinkedEventAction } from '@/lib/api/events'
import {
  actionsApi,
  type Action,
  actionLifecycleStateLabels,
  actionLifecycleStateColors,
} from '@/lib/api/actions'
import { categoriesApi } from '@/lib/api/categories'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { LinkPickerDialog, type PickerTreeNode } from '@/components/shared/LinkPickerDialog'
import { ConfirmUnlinkDialog } from '@/components/shared/ConfirmUnlinkDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Zap,
  Calendar,
  AlertCircle,
  ExternalLink,
  Plus,
  Unlink,
} from 'lucide-react'
import { cn } from '@/lib/utils'

export function EventActionsTab() {
  const { slug } = useTenant()
  const { eventId } = useParams({ strict: false }) as { eventId: string }
  const navigate = useNavigate()
  const api = useApi()
  const queryClient = useQueryClient()
  const evtApi = eventsApi(api)

  const [pickerOpen, setPickerOpen] = useState(false)
  const [unlinkTarget, setUnlinkTarget] = useState<LinkedEventAction | null>(null)

  // Linked actions for this event
  const {
    data: actions,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.events.actions(slug, eventId),
    queryFn: () => evtApi.actions(eventId),
  })

  // Action category tree for the picker
  const { data: categoryTree, isLoading: categoriesLoading } = useQuery({
    queryKey: queryKeys.categories.tree(slug, 'action'),
    queryFn: () => categoriesApi(api, 'action').tree(),
    enabled: pickerOpen,
  })

  // All actions (permission-filtered)
  const { data: allActions, isLoading: actionsLoading } = useQuery({
    queryKey: queryKeys.actions.all(slug),
    queryFn: () => actionsApi(api).list(),
    enabled: pickerOpen,
  })

  const existingIds = useMemo(
    () => new Set(actions?.map((a) => a.action_id) ?? []),
    [actions],
  )

  // Unlink mutation
  const unlinkMutation = useMutation({
    mutationFn: ({ linkId, notes }: { linkId: string; notes: string }) =>
      evtApi.unlinkAction(eventId, linkId, notes || undefined),
    onSuccess: () => {
      setUnlinkTarget(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.events.actions(slug, eventId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.events.detail(slug, eventId) })
    },
  })

  if (isLoading) return <TableSkeleton rows={4} />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load linked actions"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {actions?.length ?? 0} linked action{actions?.length !== 1 ? 's' : ''}
        </div>
        <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          Link Action
        </Button>
      </div>

      {/* Empty state */}
      {(!actions || actions.length === 0) && (
        <div className="flex h-48 items-center justify-center text-muted-foreground">
          <div className="space-y-2 text-center">
            <Zap className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm">No actions are linked to this event.</p>
            <p className="text-xs text-muted-foreground/60">
              Use the button above to link actions to this event.
            </p>
          </div>
        </div>
      )}

      {/* Action list */}
      {actions && actions.length > 0 && (
        <div className="divide-y rounded-lg border">
          {actions.map((action) => (
            <ActionRow
              key={action.link_id}
              action={action}
              onClick={() => navigate({ to: `/t/${slug}/actions/${action.action_id}` })}
              onUnlink={() => setUnlinkTarget(action)}
            />
          ))}
        </div>
      )}

      {/* Link picker dialog */}
      <LinkPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onLinked={() => {
          setPickerOpen(false)
          queryClient.invalidateQueries({ queryKey: queryKeys.events.actions(slug, eventId) })
          queryClient.invalidateQueries({ queryKey: queryKeys.events.detail(slug, eventId) })
        }}
        title="Link Action"
        description="Select an action to link to this event."
        treeNodes={categoryTree as PickerTreeNode[] | undefined}
        objects={allActions}
        isLoading={actionsLoading || categoriesLoading}
        existingIds={existingIds}
        getObjectId={(a: Action) => a.id}
        getObjectParentId={(a: Action) => a.category_id}
        getObjectSearchText={(a: Action) => `${a.title} ${a.category_name}`}
        renderObject={(a: Action, isSelected: boolean) => (
          <ActionPickerRow action={a} isSelected={isSelected} />
        )}
        linkFn={(actionId: string) => evtApi.linkAction(eventId, actionId)}
        linkButtonLabel="Link Action"
        searchPlaceholder="Search actions or categories..."
      />

      {/* Unlink confirmation */}
      {unlinkTarget && (
        <ConfirmUnlinkDialog
          open={!!unlinkTarget}
          onClose={() => setUnlinkTarget(null)}
          onConfirm={(notes) =>
            unlinkMutation.mutate({ linkId: unlinkTarget.link_id, notes })
          }
          title="Remove Action Link"
          description={`Remove the link between this event and "${unlinkTarget.action_title}"?`}
          isPending={unlinkMutation.isPending}
        />
      )}
    </div>
  )
}

function ActionRow({
  action,
  onClick,
  onUnlink,
}: {
  action: LinkedEventAction
  onClick: () => void
  onUnlink: () => void
}) {
  return (
    <div
      className="group flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors hover:bg-accent/30"
      onClick={onClick}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{action.action_title}</span>
          <ExternalLink className="h-3 w-3 flex-shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />
        </div>
      </div>

      {/* Due date + overdue */}
      <div className="hidden items-center gap-1 text-xs text-muted-foreground md:flex">
        <Calendar className="h-3 w-3" />
        <span>
          {action.due_date
            ? new Date(action.due_date + 'T00:00:00').toLocaleDateString()
            : 'No due date'}
        </span>
        {action.is_overdue && (
          <AlertCircle className="ml-1 h-3.5 w-3.5 text-red-500" />
        )}
      </div>

      {/* Linked date */}
      <div className="hidden items-center gap-1 text-xs text-muted-foreground xl:flex">
        <span>Linked {new Date(action.linked_at).toLocaleDateString()}</span>
      </div>

      <Badge
        variant="outline"
        className={cn(
          'flex-shrink-0 px-1.5 py-0 text-xs',
          actionLifecycleStateColors[action.lifecycle_state] ?? 'bg-gray-100 text-gray-700',
        )}
      >
        {actionLifecycleStateLabels[action.lifecycle_state] ?? action.lifecycle_state}
      </Badge>

      <button
        className="flex-shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
        onClick={(e) => {
          e.stopPropagation()
          onUnlink()
        }}
        title="Remove link"
      >
        <Unlink className="h-4 w-4" />
      </button>
    </div>
  )
}

function ActionPickerRow({
  action,
  isSelected,
}: {
  action: Action
  isSelected: boolean
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className={cn('truncate', isSelected && 'font-medium')}>{action.title}</span>
      <Badge
        variant="outline"
        className={cn(
          'ml-auto flex-shrink-0 px-1.5 py-0 text-xs',
          actionLifecycleStateColors[action.lifecycle_state] ?? 'bg-gray-100 text-gray-700',
        )}
      >
        {actionLifecycleStateLabels[action.lifecycle_state] ?? action.lifecycle_state}
      </Badge>
    </div>
  )
}
